import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Utensils, Scale, Gauge } from 'lucide-react';
import { RecordCard, MetricBox, StatusBadge } from '@/features/shared/components/records';
import type { FeedRecord } from '@/api/feedApi';
import { format } from 'date-fns';

interface FeedRecordCardProps {
  record: FeedRecord;
  farmId: string;
  pondName?: string;
}

const appetiteStars = (appetite: number): string => {
  const stars = Math.round(appetite);
  return '⭐'.repeat(Math.min(stars, 5));
};

const appetiteColor = (appetite: number): 'emerald' | 'amber' | 'red' => {
  if (appetite >= 4) return 'emerald';
  if (appetite >= 2) return 'amber';
  return 'red';
};

const fcrColor = (fcr: number | null | undefined): 'emerald' | 'amber' | 'red' | 'blue' => {
  if (fcr == null) return 'blue';
  if (fcr < 1.5) return 'emerald';
  if (fcr < 2.0) return 'amber';
  return 'red';
};

export const FeedRecordCard: React.FC<FeedRecordCardProps> = ({ record, farmId, pondName }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/farms/${farmId}/feed/${record.id}`);
  };

  return (
    <RecordCard
      icon={Utensils}
      title="Feed Record"
      subtitle={`${pondName || record.pondName || `Pond ${record.pondId.slice(0, 8)}`} • ${format(new Date(record.feedTime), 'PPpp')}`}
      topRight={
        <>
          <StatusBadge variant="info">{record.feedBrand}</StatusBadge>
          <StatusBadge variant="info">{record.feedType}</StatusBadge>
        </>
      }
      metricsGrid={
        <>
          <MetricBox
            label="Total Feed"
            value={`${(record.totalFeed || 0).toFixed(1)}kg`}
            icon={Utensils}
            color="blue"
            subtext={`Max: ${(record.maximumFeed || 0).toFixed(1)}kg`}
          />
          <MetricBox
            label="Appetite"
            value={appetiteStars(record.appetite || 0)}
            color={appetiteColor(record.appetite || 0)}
            subtext={`${(record.appetite || 0).toFixed(1)}/5`}
          />
          <MetricBox
            label="FCR"
            value={record.feedConversionRatio != null ? record.feedConversionRatio.toFixed(2) : '—'}
            icon={Scale}
            color={fcrColor(record.feedConversionRatio)}
          />
          <MetricBox
            label="Feed Used"
            value={`${(record.maximumFeed > 0 ? ((record.totalFeed / record.maximumFeed) * 100) : 0).toFixed(0)}%`}
            icon={Gauge}
            color={
              record.maximumFeed > 0 && record.totalFeed / record.maximumFeed > 0.9
                ? 'emerald'
                : record.maximumFeed > 0 && record.totalFeed / record.maximumFeed > 0.6
                ? 'amber'
                : 'red'
            }
          />
        </>
      }
      footer={
        record.notes ? (
          <p className="line-clamp-2">{record.notes}</p>
        ) : (
          <p className="italic text-slate-400">No notes</p>
        )
      }
      onClick={handleClick}
    />
  );
};
